import deepmerge from 'deepmerge';
import React, { createContext, useMemo } from 'react';
import { Consumer } from '~/constants/common/consumer';
import { ClientId } from '~/constants/Events';
import type { Configuration, Cookies } from '~/contracts/common';
import type { DeepPartial } from '~/types';

export type ConfigurationForUi = Pick<
  Configuration,
  | 'dataDogRecordingSampleRate'
  | 'dataDogSampleRate'
  | 'enableAutoShowcase'
  | 'enableCvnaFeatures'
  | 'EvTaxCreditTagConfig'
  | 'priceDropRestrictedStates'
  | 'useNewCarvanaCarePdf'
>;

type ContextType = {
  clientId: ClientId;
  consumer: Consumer | undefined;
  configuration: ConfigurationForUi;
  eventCorrelationId: string;
  referrer: string;
  removeFooter: boolean;
  removeHeader: boolean;
  serverCookies: Cookies;
};

const defaultContext: ContextType = {
  clientId: ClientId.DEFAULT,
  consumer: undefined,
  configuration: {
    enableAutoShowcase: false,
    enableCvnaFeatures: false,
    EvTaxCreditTagConfig: {
      evTaxCreditTagId: '116',
      isUsingThreshold: false,
      maxPriceThreshold: 25000
    },
    priceDropRestrictedStates: [],
    useNewCarvanaCarePdf: false
  },
  eventCorrelationId: '',
  referrer: '',
  removeFooter: false,
  removeHeader: false,
  serverCookies: {}
};

const StaticContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<ContextType>;

export function StaticContextProvider({ children, ...props }: OwnProps) {
  const value = useMemo<ContextType>(
    () => ({
      ...props,
      configuration: {
        ...props.configuration,
        priceDropRestrictedStates:
          props.configuration.priceDropRestrictedStates ?? []
      }
    }),
    [props]
  );

  return (
    <StaticContext.Provider value={value}>{children}</StaticContext.Provider>
  );
}

StaticContextProvider.displayName = 'StaticContextProvider';

export const generateStaticContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useConfiguration() {
  return React.useContext(StaticContext).configuration;
}

export function useReferrer() {
  return React.useContext(StaticContext).referrer;
}

export function useConsumer(): Consumer {
  return React.useContext(StaticContext).consumer as Consumer;
}

export default StaticContext;
